/**
 * The primary metric of a campaign: the unit of its target `T`, the creator threshold `M` and
 * every creator's score (specification §4.2, §8.4).
 */

import { creatorThreshold } from "@morcast/protocol";

import type { Platform, YoutubeFormat } from "./terms.js";

/** How a campaign's performance is counted. */
export interface PrimaryMetric {
  /** The unit in plain words, e.g. "qualified X impressions". */
  unit: string;
  /** What Morcast counts, as one sentence. */
  description: string;
}

/** The primary metric of a platform and format. */
export function primaryMetric(platform: Platform, format: YoutubeFormat | null): PrimaryMetric {
  if (platform === "X") {
    return {
      unit: "qualified X impressions",
      description:
        "Morcast counts the organic impressions of each post, read with the creator's authorization when the campaign ends.",
    };
  }
  if (format === "INTEGRATION") {
    return {
      unit: "retention-adjusted brand views",
      description:
        "Morcast counts organic views during the reporting dates, weighted by how much of the branded segment viewers watched.",
    };
  }
  return {
    unit: "qualified brand views",
    description:
      "Morcast counts the organic views of each video during the reporting dates. Advertising traffic never counts.",
  };
}

/**
 * The creator threshold `M = ceil(T / 100)`: a creator whose total is below it scores 0. The
 * protocol SDK's implementation, so the platform and every verifier agree.
 */
export function threshold(target: bigint): bigint {
  return creatorThreshold(target);
}
